import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import AnalystBroNavbar from "../components/AnalystBroNavbar";
import AnalystBroFooter from "../components/AnalystBroFooter";
import DesktopDownloads from "../components/DesktopDownloads";
import FeedbackForm from "../components/FeedbackForm";
import { ClaudeLogo, OpenAILogo, AntigravityLogo } from "../components/ProviderLogos";
import { BLOG_POSTS } from "../data/blogPosts";
import {
  ArrowRight,
  CandlestickChart,
  FileSearch,
  ListFilter,
  FlaskConical,
  Briefcase,
  BellRing,
  Smartphone,
  KeyRound,
  Monitor,
  Plug,
  ShieldCheck,
} from "lucide-react";

// The whole homepage uses one colour set from index.css. Light is the default:
// pages people publish later will sit on this look.
const THEME = "ab-light";

// The Web MCP (plan 282/283): what an AI chat app can do once AnalystBro is connected.
// Keep this to what the connector really exposes (mcp_server/web_mcp.py WEB_MCP_TOOLS).
const CONNECTOR_CAN = [
  ["Charts", "TradingView charts with Bollinger Bands and RSI, the price bars behind them, and technical metrics like RSI and distance from the 52-week high."],
  ["Filings", "Search company reports and open the exact page an answer comes from."],
  ["Screener", "Filter stocks by industry, valuation, growth and price behaviour."],
  ["Datasets", "Bangladesh public data and publications, such as BBS and Bangladesh Bank releases."],
];

// Two ways to use AnalystBro. Keep in step with what each really does
// (desktop: src-tauri tools; connector: mcp_server/web_mcp.py WEB_MCP_TOOLS).
const TWO_WAYS = [
  {
    icon: Monitor,
    name: "Desktop app",
    tag: "Full research agent",
    line: "Install it on Mac or Windows. It runs on your computer with the Claude, ChatGPT or Antigravity plan you already have.",
    does: [
      "Charts, filings, screener and Bangladesh datasets",
      "Backtesting and paper trading",
      "TradingView charts it draws on",
      "Alerts, watchlists and portfolio",
      "Use it from your phone with remote access",
    ],
    cta: ["Get the desktop app", "#download"],
  },
  {
    icon: Plug,
    name: "MCP connector",
    tag: "Inside your AI chat app",
    line: "Nothing to install. Add AnalystBro to Claude.ai, ChatGPT or Gemini and ask there.",
    does: [
      "Charts, filings, screener and Bangladesh datasets",
      "Market data only: no backtesting, alerts or your own data",
    ],
    cta: ["Set up the connector", "#ai-apps"],
  },
];

const CONNECTOR_STEPS = [
  ["Get your connection details", "In Settings → AI apps, create your Client ID and secret. The page also shows the server address."],
  ["Add AnalystBro as a custom connector", "Claude.ai: Settings → Connectors. Gemini: Customize → Connectors → + Custom. ChatGPT: a custom app in developer mode. Paste the address, Client ID and secret."],
  ["Sign in and allow", "Sign in with your AnalystBro account and allow access. Then just ask, e.g. “chart Grameenphone weekly”."],
];

const CAPABILITIES = [
  {
    icon: CandlestickChart,
    title: "Interactive charts",
    body: "Ask for any ticker and get technical charts with indicators, patterns, and context on demand.",
  },
  {
    icon: FileSearch,
    title: "Filings & reports",
    body: "Read annual reports and filings, then generate clean, cited research write-ups in one place.",
  },
  {
    icon: ListFilter,
    title: "Screening",
    body: "Describe what you're hunting for in plain English and the agent runs the screener for you.",
  },
  {
    icon: FlaskConical,
    title: "Backtesting & paper trading",
    body: "Test a trading idea on years of data with costs included, then run it in paper mode on new data.",
  },
  {
    icon: Briefcase,
    title: "Watchlists & portfolio",
    body: "Track the names you care about and get context on your holdings without leaving the chat.",
  },
  {
    icon: BellRing,
    title: "Alerts",
    body: "Set up alerts conversationally and let the agent watch the market so you don't have to.",
  },
];

// Real answers from the desktop app. Each question is quoted as the user asked it.
const SHOWCASE = [
  {
    title: "Technical charting agent",
    body: "Ask for any ticker and get technical charts with indicators, patterns, and context on demand. The agent opens the chart in TradingView and draws its support, resistance and trendlines on it.",
    ask: "DSEBD: SQURPHARMA — give me the full technical read: support, resistance, trendlines…",
    img: "/images/app-chart.webp",
    alt: "A TradingView daily chart of Square Pharmaceuticals with support, resistance and trendlines drawn by AnalystBro",
  },
  {
    title: "Strategy backtesting",
    body: "Pick an idea from AnalystBro's strategy library, or describe your own. The agent writes it as code, backtests it on your computer with trading costs, and compares it with buy-and-hold. The report says where the strategy fails, not just how much it made.",
    ask: "Backtest the 200-day moving average month-end timing idea from the strategy proposal library on SPY from 2005 to today. Compare it with buy-and-hold, show me where it fails, and make a results page.",
    img: "/images/app-backtest.webp",
    alt: "AnalystBro backtest results for a 200-day moving average strategy on SPY: total return, CAGR, max drawdown and Sharpe against buy-and-hold, an equity curve on a log scale and yearly returns",
  },
  {
    title: "Paper trading",
    body: "Deploy a tested strategy in paper mode. After each market close, while the app is open, it runs on the new data and you get a notification for every signal. A simulated account follows those signals with the same costs, so you can see how the strategy does going forward. No real trades are placed.",
    ask: "Deploy strategy proposal OSB-01 (4-EMA score position control) on SPY in paper mode",
    img: "/images/app-paper.webp",
    alt: "An AnalystBro paper account: account value, return since the start against SPY, cash and open positions",
  },
  {
    title: "Filing search agent",
    body: "Read annual reports and filings, then generate clean, cited research write-ups in one place. The hard parts too — NPL, loan classification and Basel III capital buried in a bank's notes — with the page for every figure.",
    ask: "From BRAC Bank's 2025 annual report, find the gross NPL ratio, the classified loan breakdown and the Basel III capital ratios from the notes. Cite the page for every figure.",
    img: "/images/app-bank.webp",
    alt: "AnalystBro answering from BRAC Bank's 2025 annual report: the capital adequacy page, then a table of NPL, loan classification, CET1, Tier-1 and CRAR with page citations",
  },
  {
    title: "Remote chat from your phone",
    body: "Turn on remote access in the desktop app, then log in on your phone. Your questions run on the agent at your desk — with its charts, data and your own AI plan.",
    ask: "show RSI and support on the BRACBANK chart",
    img: "/images/app-remote.webp",
    alt: "AnalystBro on a phone: the agent's answer with RSI, support levels and a BRAC Bank chart",
    phone: true,
  },
  {
    title: "Research alerts",
    body: "Set up alerts conversationally and let the agent watch the market so you don't have to. On schedule it researches the news on your stocks and sends you a written briefing.",
    ask: "Check for notable news on AAPL",
    img: "/images/app-alert.webp",
    alt: "An AnalystBro alert briefing: notable news and developments for Apple, grouped by theme",
  },
  {
    title: "Compliance & regulation",
    body: "Search Bangladesh Bank circulars and BSEC laws, rules and directives. Trace how a rule changed over time and which circulars apply — with the reference number for every change.",
    ask: "show the timeline of BSEC margin loan rule changes",
    img: "/images/app-compliance.webp",
    alt: "AnalystBro's timeline of BSEC margin loan rule changes from 1999, each with its notification reference",
  },
];

const eyebrow = "font-mono text-xs uppercase tracking-[0.12em] text-[var(--ab-accent)]";
const h2 = "font-display text-3xl sm:text-5xl font-bold tracking-tight leading-[1.08]";
const btnPrimary =
  "inline-flex items-center justify-center gap-2 rounded-lg bg-[var(--ab-accent)] px-6 min-h-[48px] font-semibold text-[var(--ab-on-accent)] hover:bg-[var(--ab-accent-hover)] transition";
const textLink = "font-semibold text-[var(--ab-accent)] hover:underline";

export default function AnalystBroHome() {
  const { isAuthenticated } = useAuth();
  const recentPosts = BLOG_POSTS.slice(0, 3);
  const { hash } = useLocation();

  // Scroll to #contact / #download, including after a client-side redirect.
  useEffect(() => {
    if (!hash) return;
    const t = setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth" }), 100);
    return () => clearTimeout(t);
  }, [hash]);

  return (
    <div className={`${THEME} min-h-screen flex flex-col font-sans antialiased`}>
      <AnalystBroNavbar theme={THEME} />

      <main className="flex-1">
        {/* Hero, computer: one message, one action, the real app below. */}
        <section className="hidden md:flex max-w-6xl mx-auto px-8 pt-28 flex-col items-center text-center gap-7">
          <h1 className="font-display text-7xl font-bold tracking-tight leading-[1.02] max-w-4xl">
            Your AI equity-research agent
          </h1>
          <p className="text-lg text-[var(--ab-muted)] max-w-xl leading-relaxed">
            Charts, filings, screening, backtesting and reports — all in one chat. AnalystBro does the legwork so you can focus on the decision.
          </p>
          <div className="flex items-center gap-6 mt-2">
            <a href="#download" className={btnPrimary}>Get the desktop app</a>
            <a href="#two-ways" className={textLink}>Desktop app or MCP connector? ↓</a>
          </div>
          <img
            src="/images/app-map.webp"
            alt="The AnalystBro desktop app answering a question with an interactive map of Bangladesh"
            className="mt-14 w-full max-w-5xl rounded-xl border border-[var(--ab-border)]"
            style={{ boxShadow: "var(--ab-shadow)" }}
          />
        </section>

        {/* Hero, phone: the job here is logging in to the agent on your computer. */}
        <section className="md:hidden px-4 pt-12 pb-4 flex flex-col gap-5">
          <span className={eyebrow}>AnalystBro on your phone</span>
          <h1 className="font-display text-4xl font-bold tracking-tight leading-[1.05]">
            Your research agent, in your pocket
          </h1>
          <p className="text-[var(--ab-muted)] leading-relaxed">
            Log in to chat with the AnalystBro agent running on your computer — on your own Claude or ChatGPT plan.
          </p>
          <Link to={isAuthenticated ? "/agentbro" : "/login"} className={`${btnPrimary} w-full min-h-[52px] text-base`}>
            {isAuthenticated ? "Open AnalystBro" : "Log in"}
          </Link>
          <div className="flex gap-3 p-4 rounded-xl border border-[var(--ab-border)] bg-[var(--ab-surface)] text-sm text-[var(--ab-muted)] leading-relaxed">
            <Monitor size={20} className="shrink-0 text-[var(--ab-accent)]" />
            <span>
              Works when AnalystBro is open on your computer with <b className="text-[var(--ab-text)]">remote access on</b>.
              Not set up yet? <a href="#how" className="text-[var(--ab-accent)]">See how it works</a>.
            </span>
          </div>
        </section>

        {/* Two ways to use it: the desktop app vs the MCP connector, side by side. */}
        <section id="two-ways" className="scroll-mt-16 border-t border-[var(--ab-border)]">
          <div className="max-w-6xl mx-auto px-4 sm:px-8 py-20 md:py-24 flex flex-col gap-10">
            <div className="flex flex-col gap-4 max-w-2xl">
              <span className={eyebrow}>Two ways to use AnalystBro</span>
              <h2 className={h2}>Desktop app or MCP connector</h2>
              <p className="text-[var(--ab-muted)] leading-relaxed">
                Both use the same AnalystBro data and the same account. The desktop app is the full agent; the
                connector brings the market data into the AI chat app you already use.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {TWO_WAYS.map(({ icon: Icon, name, tag, line, does, cta }) => (
                <div key={name} className="flex flex-col gap-4 rounded-xl border border-[var(--ab-border)] bg-[var(--ab-surface)] p-6">
                  <div className="flex items-center gap-3">
                    <span className="w-11 h-11 rounded-xl bg-[var(--ab-accent-soft)] text-[var(--ab-accent)] flex items-center justify-center">
                      <Icon size={22} strokeWidth={1.8} />
                    </span>
                    <div className="flex flex-col">
                      <h3 className="text-xl font-bold">{name}</h3>
                      <span className="text-sm text-[var(--ab-muted)]">{tag}</span>
                    </div>
                  </div>
                  <p className="text-[var(--ab-muted)] leading-relaxed">{line}</p>
                  <ul className="flex flex-col gap-2 text-sm">
                    {does.map((d) => <li key={d} className="flex gap-2"><span className="text-[var(--ab-accent)]">•</span>{d}</li>)}
                  </ul>
                  <a href={cta[1]} className={`${textLink} mt-auto`}>{cta[0]} →</a>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* AI app connector (Web MCP): use AnalystBro inside Claude.ai, ChatGPT and Gemini. */}
        <section id="ai-apps" className="scroll-mt-16 border-t border-[var(--ab-border)]">
          <div className="max-w-6xl mx-auto px-4 sm:px-8 py-20 md:py-28 grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 items-start">
            <div className="flex flex-col gap-5">
              <span className={eyebrow}>MCP server · AI app connector</span>
              <h2 className={h2}>Use AnalystBro inside Claude, ChatGPT and Gemini</h2>
              <p className="text-[var(--ab-muted)] text-lg leading-relaxed">
                Add AnalystBro as a connector in the AI chat app you already use. It connects straight to
                AnalystBro on the web, so no desktop app is needed.
              </p>
              <div className="flex flex-wrap gap-2">
                {["Claude.ai", "ChatGPT", "Gemini"].map((n) => (
                  <span key={n} className="text-sm rounded-full border border-[var(--ab-border)] bg-[var(--ab-surface)] px-3 py-1">{n}</span>
                ))}
              </div>
              <div className="flex flex-col gap-1.5">
                <span className="font-mono text-[11px] uppercase tracking-wider text-[var(--ab-faint)]">Server address</span>
                <code className="font-mono text-sm rounded-lg border border-[var(--ab-border)] bg-[var(--ab-bg-alt)] px-3 py-2 w-fit">
                  https://mcp.analystbro.net/mcp
                </code>
              </div>
              <Link
                to="/settings?tab=ai-apps"
                className="inline-flex items-center gap-2 w-fit rounded-xl bg-[var(--ab-accent)] text-white font-semibold px-5 py-3 hover:opacity-90"
              >
                <Plug size={18} /> Get your connection details <ArrowRight size={18} />
              </Link>
              <p className="flex items-start gap-2 text-sm text-[var(--ab-muted)]">
                <ShieldCheck size={18} className="shrink-0 mt-0.5 text-[var(--ab-accent)]" />
                Market data only: the connector looks things up and draws charts. Your watchlists, alerts and
                portfolios stay in the desktop app. Disconnect any time in Settings.
              </p>
            </div>

            <div className="flex flex-col gap-10">
              <div className="flex flex-col">
                <span className={eyebrow}>What it can do</span>
                {CONNECTOR_CAN.map(([t, b]) => (
                  <div key={t} className="flex flex-col gap-1 py-4 border-b border-[var(--ab-border)] last:border-0">
                    <h3 className="text-lg font-bold">{t}</h3>
                    <p className="text-[var(--ab-muted)] leading-relaxed">{b}</p>
                  </div>
                ))}
              </div>
              <ol className="flex flex-col gap-6">
                <span className={eyebrow}>Set it up</span>
                {CONNECTOR_STEPS.map(([t, b], i) => (
                  <li key={t} className="grid grid-cols-[40px_1fr] gap-3">
                    <span className="font-mono text-sm text-[var(--ab-accent)] pt-0.5">0{i + 1}</span>
                    <div className="flex flex-col gap-1">
                      <h3 className="font-bold">{t}</h3>
                      <p className="text-[var(--ab-muted)] leading-relaxed">{b}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </section>

        {/* How it works: your own AI plan does the thinking. */}
        <section id="how" className="scroll-mt-16 max-w-6xl mx-auto px-4 sm:px-8 py-20 md:py-32 flex flex-col gap-12">
          <div className="flex flex-col gap-4 max-w-2xl">
            <span className={eyebrow}>How it works</span>
            <h2 className={h2}>Bring your own AI plan</h2>
            <p className="text-[var(--ab-muted)] leading-relaxed">
              AnalystBro doesn’t resell AI. It runs on your computer with the Claude, ChatGPT/Codex or Antigravity
              subscription you already have, and adds the market data, charts and filings.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[1fr_96px_1.15fr_96px_1fr] items-center">
            <FlowNode icon={<Smartphone size={26} />} title="You ask" sub="on your phone or computer" />
            <div className="ab-pipe md:mx-2" aria-hidden="true"><i /></div>
            <FlowNode
              hub
              icon={<img src="/images/analystbro-logo.png" alt="" className="h-8 w-8" />}
              title="AnalystBro desktop"
              sub="adds charts, filings & data"
            />
            <div className="ab-pipe md:mx-2" aria-hidden="true"><i style={{ animationDelay: "0.9s" }} /></div>
            <FlowNode
              icon={<KeyRound size={26} />}
              title="Your AI plan"
              sub={
                <span className="flex items-center justify-center gap-5 mt-2">
                  <ClaudeLogo className="h-7 w-7" />
                  <OpenAILogo className="h-7 w-7 text-[var(--ab-text)]" />
                  <AntigravityLogo className="h-7 w-7" />
                </span>
              }
            />
          </div>

          <ol className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              ["Install the desktop app", "Mac or Windows. The app walks you through installing an AI coding agent on first run."],
              ["Sign in to your AI", "Use the Claude, ChatGPT or Antigravity account you already pay for."],
              ["Ask — anywhere", "On the computer, or turn on remote access and log in from your phone."],
            ].map(([t, b], i) => (
              <li key={t} className="flex flex-col gap-2">
                <span className="font-mono text-sm text-[var(--ab-accent)]">0{i + 1}</span>
                <h3 className="text-lg font-bold">{t}</h3>
                <p className="text-[var(--ab-muted)] leading-relaxed">{b}</p>
              </li>
            ))}
          </ol>
        </section>

        {/* See it in action: one real answer per core use. */}
        <section className="border-t border-[var(--ab-border)] bg-[var(--ab-bg-alt)]">
          <div className="max-w-6xl mx-auto px-4 sm:px-8 py-20 md:py-28 flex flex-col gap-16 md:gap-24">
            <div className="flex flex-col gap-4 max-w-2xl">
              <span className={eyebrow}>See it in action</span>
              <h2 className={h2}>Real answers from the desktop app</h2>
            </div>
            {SHOWCASE.map((x, i) => (
              <div key={x.title} className={`grid grid-cols-1 gap-8 md:gap-14 items-center ${i % 2 ? "md:grid-cols-[7fr_5fr]" : "md:grid-cols-[5fr_7fr]"}`}>
                <div className={`flex flex-col gap-4 ${i % 2 ? "md:order-2" : ""}`}>
                  <span className="font-mono text-sm text-[var(--ab-accent)]">0{i + 1}</span>
                  <h3 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">{x.title}</h3>
                  <p className="text-[var(--ab-muted)] leading-relaxed">{x.body}</p>
                  <p className="text-sm rounded-xl border border-[var(--ab-border)] bg-[var(--ab-surface)] px-4 py-3">
                    <span className="block font-mono text-[11px] uppercase tracking-wider text-[var(--ab-faint)] mb-1">Asked</span>
                    “{x.ask}”
                  </p>
                </div>
                <img
                  src={x.img}
                  alt={x.alt}
                  loading="lazy"
                  className={`rounded-xl border border-[var(--ab-border)] ${x.phone ? "w-full max-w-[340px] mx-auto rounded-[28px] border-[6px] border-[#1c2230]" : "w-full"} ${i % 2 ? "md:order-1" : ""}`}
                  style={{ boxShadow: "var(--ab-shadow)" }}
                />
              </div>
            ))}
          </div>
        </section>

        {/* Capabilities */}
        <section className="border-t border-[var(--ab-border)]">
          <div className="max-w-6xl mx-auto px-4 sm:px-8 py-20 md:py-28 grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 items-start">
            <div className="flex flex-col gap-4 md:sticky md:top-28">
              <h2 className={h2}>One agent. Every research capability.</h2>
              <p className="text-[var(--ab-muted)] text-lg leading-relaxed">
                Move between screening, charting, filing analysis and backtesting in the same chat.
              </p>
            </div>
            <div className="flex flex-col">
              {CAPABILITIES.map(({ icon: Icon, title, body }) => (
                <div key={title} className="grid grid-cols-[48px_1fr] gap-4 py-6 border-b border-[var(--ab-border)] last:border-0">
                  <span className="w-11 h-11 rounded-xl bg-[var(--ab-accent-soft)] text-[var(--ab-accent)] flex items-center justify-center">
                    <Icon size={22} strokeWidth={1.8} />
                  </span>
                  <div className="flex flex-col gap-1.5">
                    <h3 className="text-lg font-bold">{title}</h3>
                    <p className="text-[var(--ab-muted)] leading-relaxed">{body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <DesktopDownloads />

        {/* Enterprise: one line and a contact button, nothing more (user, 2026-10-22). */}
        <section id="enterprise" className="scroll-mt-16 border-t border-[var(--ab-border)]">
          <div className="max-w-6xl mx-auto px-4 sm:px-8 py-20 md:py-24 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
            <div className="flex flex-col gap-4 max-w-2xl">
              <span className={eyebrow}>Enterprise</span>
              <h2 className={h2}>Transform your employee and customers investment journey with AI</h2>
            </div>
            <a href="#contact" className={`${btnPrimary} shrink-0`}>
              Contact us <ArrowRight size={16} />
            </a>
          </div>
        </section>

        {/* Research desk: full cards once there are posts, one line until then. */}
        <section className="max-w-6xl mx-auto px-4 sm:px-8">
          {recentPosts.length > 0 ? (
            <div className="flex flex-col gap-8 pb-8">
              <div className="flex items-end justify-between gap-4">
                <h2 className={h2}>Latest Quantitative Insights</h2>
                <Link to="/blog" className={textLink}>All articles →</Link>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {recentPosts.map((post) => (
                  <Link key={post.id} to={`/blog/${post.slug}`} className="flex flex-col gap-3 border-t-2 border-[var(--ab-accent)] pt-5">
                    <span className="font-mono text-[11px] uppercase text-[var(--ab-faint)]">{post.category} · {post.readTime}</span>
                    <h3 className="font-display text-xl font-bold leading-snug">{post.title}</h3>
                    <p className="text-sm text-[var(--ab-muted)] line-clamp-3">{post.summary}</p>
                  </Link>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap items-center justify-between gap-6 py-8 border-y border-[var(--ab-border)]">
              <div className="flex flex-col gap-2 max-w-2xl">
                <span className={eyebrow}>AnalystBro Research Desk · coming soon</span>
                <p className="text-[var(--ab-muted)] leading-relaxed">
                  Our research team is drafting in-depth whitepapers on AI equity research, filings and strategy testing.
                </p>
              </div>
              <Link to="/blog" className={textLink}>Visit Research Desk →</Link>
            </div>
          )}
        </section>

        <FeedbackForm />
      </main>

      <AnalystBroFooter theme={THEME} />
    </div>
  );
}

function FlowNode({ icon, title, sub, hub = false }) {
  return (
    <div
      className={`flex flex-col items-center text-center gap-2 px-5 py-7 rounded-2xl border bg-[var(--ab-surface)] ${
        hub ? "border-[var(--ab-accent)] ring-8 ring-[var(--ab-accent-soft)]" : "border-[var(--ab-border)]"
      }`}
    >
      <span className="text-[var(--ab-muted)]">{icon}</span>
      <b className="text-base">{title}</b>
      <span className="text-sm text-[var(--ab-muted)]">{sub}</span>
    </div>
  );
}
